import { TripLeg, TripStop } from '../types/trip';
import { dateForTripDay, segmentIntoDays, TripDay } from './tripPlanner';

/**
 * tripPrint.ts — build a self-contained, print-friendly HTML day-sheet for a
 * trip. Opened in a new window from the planner's "Print" action; pure so it
 * can be unit tested. Stop names and notes are USER INPUT — always escaped.
 */

export interface TripPrintInput {
  stops: TripStop[];
  legs: TripLeg[];
  startDate?: string | null;
  maxDriveMinutesPerDay?: number;
  routeName?: string;
  totalDistanceKm?: number;
  totalDriveMinutes?: number;
  /** Google Maps directions deep-link for the whole trip, if available. */
  mapsUrl?: string | null;
  brandName?: string;
  siteUrl?: string;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const formatDrive = (minutes?: number): string => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
};

const formatKm = (km?: number): string =>
  typeof km === 'number' && !Number.isNaN(km) ? `${km.toFixed(km >= 100 ? 0 : 1)} km` : '';

const formatLongDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const STOP_KIND_LABELS: Record<string, string> = {
  start: 'Start',
  end: 'Destination',
  camp: 'Campsite',
  poi: 'Point of interest',
  waypoint: 'Waypoint',
};

function renderStopRow(stop: TripStop, letter: string, legAfter?: TripLeg): string {
  const kind = STOP_KIND_LABELS[stop.kind] ?? 'Stop';
  const legBits = [formatKm(legAfter?.distanceKm), formatDrive(legAfter?.driveMinutes)]
    .filter(Boolean)
    .join(' · ');
  return `
      <div class="stop">
        <div class="stop-head">
          <span class="badge">${escapeHtml(letter)}</span>
          <span class="stop-name">${escapeHtml(stop.name)}</span>
          <span class="stop-kind">${escapeHtml(kind)}</span>
        </div>
        ${stop.notes ? `<div class="stop-notes">${escapeHtml(stop.notes)}</div>` : ''}
        ${legBits ? `<div class="leg">↓ &nbsp;${escapeHtml(legBits)} drive</div>` : ''}
      </div>`;
}

export function buildTripPrintHtml(input: TripPrintInput): string {
  const {
    stops,
    legs,
    startDate = null,
    maxDriveMinutesPerDay = 360,
    routeName,
    totalDistanceKm,
    totalDriveMinutes,
    mapsUrl,
    brandName = 'Camping Australia',
    siteUrl = 'campingaustralia.com.au',
  } = input;

  // The same days as the planner list and the map's colours (segmentIntoDays:
  // a night booked at a stop ends the day). Days are numbered by the
  // calendar, so a rest day at a 2-night stop skips a number, as in the list.
  const days: TripDay[] = segmentIntoDays(stops, legs, maxDriveMinutesPerDay);
  const dayCount = days.length ? days[days.length - 1].dayOffset + 1 : 0;
  const legByFrom = new Map<string, TripLeg>();
  legs.forEach((l) => legByFrom.set(l.fromId, l));
  const letterFor = (index: number) => String.fromCharCode(65 + (index % 26));

  const title = routeName?.trim()
    ? routeName.trim()
    : stops.length >= 2
      ? `${stops[0]?.name ?? 'Start'} to ${stops[stops.length - 1]?.name ?? 'Finish'}`
      : 'Road trip';

  const totalsBits = [
    stops.length ? `${stops.length} stop${stops.length === 1 ? '' : 's'}` : '',
    dayCount > 1 ? `${dayCount} days` : '',
    formatKm(totalDistanceKm),
    formatDrive(totalDriveMinutes),
  ]
    .filter(Boolean)
    .join(' · ');

  const daySections = days.length
    ? days
        .map((day) => {
          const date = formatLongDate(dateForTripDay(startDate, day.dayOffset));
          const dayBits = [formatKm(day.distanceKm), formatDrive(day.driveMinutes)]
            .filter(Boolean)
            .join(' · ');
          // Each day lists its origin stop through its overnight stop; the
          // origin (shared with the previous day) is only repeated as a
          // lightweight "from" line to keep the sheet compact.
          const first = day.index === 0 ? day.firstStopIndex : day.firstStopIndex + 1;
          const fromLine =
            day.index > 0
              ? `<div class="from">From ${escapeHtml(stops[day.firstStopIndex]?.name ?? '')}</div>`
              : '';
          const stopRows = stops
            .slice(first, day.lastStopIndex + 1)
            .map((stop, i) => {
              const absoluteIndex = first + i;
              const legAfter =
                absoluteIndex < day.lastStopIndex ? legByFrom.get(stop.id) : undefined;
              return renderStopRow(stop, letterFor(absoluteIndex), legAfter);
            })
            .join('');
          return `
    <section class="day">
      <h2>Day ${day.dayOffset + 1}${date ? ` — ${escapeHtml(date)}` : ''}${
        dayBits ? ` <span class="day-bits">${escapeHtml(dayBits)}</span>` : ''
      }</h2>
      ${fromLine}
      ${stopRows}
    </section>`;
        })
        .join('')
    : stops.map((stop, i) => renderStopRow(stop, letterFor(i), legByFrom.get(stop.id))).join('');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)} — day sheet</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { font: 13px/1.45 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2937; margin: 28px; }
  header { border-bottom: 2px solid #1d4ed8; padding-bottom: 10px; margin-bottom: 18px; }
  h1 { font-size: 20px; margin: 0 0 2px; }
  .totals { color: #4b5563; font-size: 12px; }
  .day { page-break-inside: avoid; margin-bottom: 16px; }
  h2 { font-size: 14px; margin: 14px 0 6px; color: #1d4ed8; }
  .day-bits { color: #6b7280; font-weight: 400; font-size: 12px; }
  .from { color: #6b7280; font-size: 11px; margin: 0 0 4px 2px; }
  .stop { margin: 0 0 6px 2px; }
  .stop-head { display: flex; align-items: baseline; gap: 6px; }
  .badge { display: inline-block; min-width: 18px; text-align: center; background: #1d4ed8; color: #fff; border-radius: 50%; font-size: 10px; font-weight: 700; line-height: 18px; }
  .stop-name { font-weight: 600; }
  .stop-kind { color: #9ca3af; font-size: 11px; }
  .stop-notes { color: #4b5563; font-size: 12px; margin: 1px 0 0 24px; white-space: pre-wrap; }
  .leg { color: #9ca3af; font-size: 11px; margin: 2px 0 4px 24px; }
  footer { margin-top: 22px; border-top: 1px solid #e5e7eb; padding-top: 8px; color: #6b7280; font-size: 11px; }
  footer a { color: #1d4ed8; word-break: break-all; }
  @media print { body { margin: 10mm; } }
</style>
</head>
<body>
  <header>
    <h1>${escapeHtml(title)}</h1>
    <div class="totals">${escapeHtml(totalsBits)}</div>
  </header>
  ${daySections}
  <footer>
    ${mapsUrl ? `Directions: <a href="${escapeHtml(mapsUrl)}">${escapeHtml(mapsUrl)}</a><br>` : ''}
    Planned with ${escapeHtml(brandName)} — ${escapeHtml(siteUrl)}
  </footer>
</body>
</html>`;
}
